import { WIDGET_DEFINITIONS, widgetTypesFor } from '@flexbyt/shared';
import { SourceGlyph, cx } from '@flexbyt/widget-preview';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { PlatformScene } from '../components/mascot/scenes.js';
import { PageHero } from '../components/ui/hero.js';
import { ButtonAnchor, ButtonLink, SiteLink } from '../components/ui/button.js';
import { Measure, Section } from '../components/ui/layout.js';
import { Badge } from '../components/ui/surface.js';
import { Eyebrow, Heading, Mark, Text } from '../components/ui/type.js';
import {
    combinationPath,
    combinationsForPlatform,
} from '../content/combinations.js';
import {
    DIRECT_INTEGRATIONS,
    integrationBySlug,
    integrationPath,
} from '../content/integrations.js';
import { platformContentBySlug } from '../content/platforms.js';
import {
    WIDGET_BUILDER_PATH,
    WIDGET_CONTENT,
    widgetPath,
} from '../content/widgets.js';
import { track } from '../lib/analytics.js';
import { breadcrumbSchema, jsonLd, seo } from '../lib/seo.js';
import { startUrl } from '../lib/site.js';

/**
 * The platform hub: "reviews on Webflow".
 *
 * ===========================================================================
 * Why this exists between the source pages and the combinations
 * ===========================================================================
 * Somebody who searches "reviews for Webflow" has said which website they have
 * and not which reviews they want. Sending them to the Google page assumes half
 * their question; sending them to `/integrations` makes them start from the
 * platform they already named.
 *
 * It is also where the footer's "Wix / Webflow / WordPress / Squarespace" links
 * have been pointing since the navigation was built, and the parent a
 * combination page needs for its breadcrumb trail to make sense.
 *
 * The page is short on purpose. Its job is to route, and the depth is one click
 * away in either direction.
 */
export const Route = createFileRoute('/for/$platform')({
  loader: ({ params }) => {
    const platform = platformContentBySlug(params.platform);
    /*
      Published only where there is something to route *to*. `/for/godaddy`
      would be a page listing nothing, which is the thin programmatic result
      this architecture exists to avoid — the platform is still supported, it
      just has no combination pages of its own yet.
    */
    if (!platform || combinationsForPlatform(platform.platform).length === 0) {
      throw notFound();
    }
    return { platform };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { platform } = loaderData;

    const { meta, links } = seo({
      title: `Review widgets for ${platform.name}`,
      description: `Show Google, Trustpilot, Yelp, Tripadvisor, Airbnb or app store reviews on your ${platform.name} site. ${platform.install}`,
      path: `/for/${platform.slug}`,
    });

    return {
      meta,
      links,
      scripts: [
        {
          type: 'application/ld+json',
          children: jsonLd(
            breadcrumbSchema([
              { name: 'Home', path: '/' },
              { name: 'Integrations', path: '/integrations' },
              { name: platform.name, path: `/for/${platform.slug}` },
            ])
          ),
        },
      ],
    };
  },
  component: PlatformHub,
});

function PlatformHub() {
  const { platform } = Route.useLoaderData();
  const combinations = combinationsForPlatform(platform.platform);
  const widgets = widgetTypesFor(platform.platform);
  const native = platform.depth !== 'embed';

  // Sources with a page for this platform, and the rest — which are still
  // supported, just without a combination page written for them yet.
  const covered = new Set(combinations.map((entry) => entry.source));
  const uncovered = DIRECT_INTEGRATIONS.filter(
    (entry) => !covered.has(entry.id)
  );

  return (
    <>
      <PageHero
        trail={[
          { name: 'Home', path: '/' },
          { name: 'Integrations', path: '/integrations' },
          { name: platform.name, path: `/for/${platform.slug}` },
        ]}
        art={<PlatformScene name={platform.name} claim={platform.claim} />}
      >
        <Measure>
          <div className="flex flex-wrap items-center gap-3">
            <Eyebrow>{platform.name}</Eyebrow>
            <Badge tone={native ? 'positive' : 'neutral'}>{platform.claim}</Badge>
          </div>

          <Heading level={1} size="hero" className="mt-4">
            Review widgets for <span className="whitespace-nowrap"><Mark>{platform.name}</Mark>.</span>
          </Heading>
          <Text size="lead" className="mt-5">
            {platform.install}
          </Text>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonAnchor
              href={startUrl(`platform-${platform.slug}`)}
              size="lg"
              onClick={() =>
                track('hero_cta_clicked', {
                  platform: platform.platform,
                  surface: `platform-${platform.slug}`,
                })
              }
            >
              Start free
            </ButtonAnchor>
            <ButtonLink to={WIDGET_BUILDER_PATH} variant="secondary" size="lg">
              Build a widget first
            </ButtonLink>
          </div>
        </Measure>
      </PageHero>

      <Section aria-labelledby="sources-heading">
        <Measure>
          <Heading level={2} id="sources-heading">
            Which reviews do you want on it?
          </Heading>
        </Measure>

        <ul className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {combinations.map((entry) => {
            const integration = integrationBySlug(`${entry.source}-reviews`)!;
            return (
              <li key={entry.source}>
                <SiteLink
                  to={combinationPath(entry)}
                  className={cx(
                    'group flex h-full flex-col rounded-xl bg-white p-5 ring-1 ring-ink-150',
                    'transition-colors duration-[var(--duration-fast)]',
                    'hover:ring-ink-300'
                  )}
                >
                  <SourceGlyph source={entry.source} className="size-6" />
                  <span className="mt-4 block text-title text-ink-950">
                    {integration.name} reviews for {platform.name}
                  </span>
                  <span className="mt-1.5 block flex-1 text-body-sm text-ink-600">
                    {entry.why}
                  </span>
                </SiteLink>
              </li>
            );
          })}
        </ul>

        {uncovered.length > 0 ? (
          <div className="mt-8 rounded-lg bg-ink-25 px-4 py-3.5 ring-1 ring-ink-150">
            <p className="text-body-sm text-ink-600">
              {/*
                Said rather than hidden. These sources work on this platform —
                the widget is the same embed — there is simply no page written
                for the pairing, and implying otherwise by omission would be its
                own small dishonesty.
              */}
              <span className="font-medium text-ink-950">
                {uncovered.map((entry) => entry.name).join(', ')}
              </span>{' '}
              work on {platform.name} too — the widget is the same. There is no
              page for those pairings yet, so start from{' '}
              {uncovered.map((entry, index) => (
                <span key={entry.slug}>
                  {index > 0 ? ', ' : ''}
                  <SiteLink
                    to={integrationPath(entry)}
                    className="font-medium text-accent-600 underline decoration-accent-200 underline-offset-[3px] hover:text-accent-700"
                  >
                    {entry.name}
                  </SiteLink>
                </span>
              ))}
              .
            </p>
          </div>
        ) : null}
      </Section>

      <Section tone="raised" aria-labelledby="widgets-heading">
        <Measure>
          <Heading level={2} id="widgets-heading">
            Layouts that work on {platform.name}.
          </Heading>
          <Text className="mt-4 max-w-prose">
            {widgets.length} of ReviewsJet&rsquo;s{' '}
            {Object.keys(WIDGET_DEFINITIONS).length}.{' '}
            {platform.platform === 'wix'
              ? 'Wix is the only platform that can run the product widget, because it needs a product page to sit on.'
              : 'Every layout except the product widget, which needs a Wix store.'}
          </Text>
        </Measure>

        <ul className="mt-8 flex flex-wrap gap-2">
          {WIDGET_CONTENT.map((widget) => (
            <li key={widget.slug}>
              <SiteLink
                to={widgetPath(widget)}
                className={cx(
                  'inline-flex rounded-lg px-3.5 py-2 text-body-sm font-medium ring-1 ring-ink-200',
                  'transition-colors duration-[var(--duration-fast)]',
                  'text-ink-700 hover:bg-white hover:text-ink-950 hover:ring-ink-300'
                )}
              >
                {widget.seoName}
              </SiteLink>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}
